import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Typography from '@mui/material/Typography';
import FormControl from '@mui/material/FormControl';
import TextField from '@mui/material/TextField';
import { GradientTypography } from 'src/components/gradients/GradientTypography';
import { useEffect, useState } from 'react';
import { useAuth } from 'src/contexts/AuthContext';
import { LoadingButton } from 'src/components/buttons/LoadingButton';
import { firestore } from 'src/config/firebaseClient';
import { usernameRegex } from 'src/util/regex';
import Button from '@mui/material/Button';
import {
  WizardElementProps,
  withoutWizardProps,
} from 'src/components/wizard/Wizard';
import { CheckInStore } from './useCheckInDialog';

export const CreateUsernameDialog = withoutWizardProps(
  ({ go }: WizardElementProps<CheckInStore>) => {
    const { user } = useAuth();
    const [submitAttempted, setSubmitAttempted] = useState(false);
    const [error, setError] = useState(false);
    const [newUsername, setNewUsername] = useState<{
      username: string;
      valid: boolean;
    }>({ username: '', valid: false });
    // TODO: Abstract out changing username into hook that is also used by profile
    const handleUsernameChange = async (
      usernameRequested: string,
    ): Promise<boolean> => {
      const { collectionGroup, getDocs, query, where, doc, setDoc } =
        await import('firebase/firestore');
      if (!user || !usernameRequested || usernameRequested?.length > 25) {
        return false;
      }
      const userCollection = collectionGroup(firestore, 'UserProfile');
      const result = await getDocs(
        query(
          userCollection,
          where('usernameLowercase', '==', usernameRequested.toLowerCase()),
        ),
      );
      if (!result.size) {
        const userDoc = doc(firestore, `User/${user?.uid}`);
        await setDoc(
          userDoc,
          {
            username: usernameRequested,
            usernameLowercase: usernameRequested.toLowerCase(),
          },
          { merge: true },
        );
        return true;
      }
      return false;
    };

    const changeUsername = async () => {
      const success = await handleUsernameChange(newUsername.username);
      if (!success) {
        setSubmitAttempted(true);
        return setError(true);
      }
      setSubmitAttempted(true);
      setError(false);
    };

    useEffect(() => {
      if (!newUsername.username) {
        return;
      }
      if (
        newUsername.username.length <= 25 &&
        newUsername.username.match(usernameRegex)
      ) {
        setNewUsername((prev) => {
          return {
            ...prev,
            valid: true,
          };
        });
      } else {
        setNewUsername((prev) => {
          return {
            ...prev,
            valid: false,
          };
        });
      }
    }, [newUsername.username]);

    return (
      <>
        <DialogTitle
          sx={{
            color: 'black',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          {
            <GradientTypography
              gradientColor="primary.vertical"
              variant="h4"
              sx={{ display: 'inline', textTransform: 'uppercase' }}
            >
              {submitAttempted && !error
                ? 'username changed'
                : 'Create a username'}
            </GradientTypography>
          }
        </DialogTitle>
        <DialogContent
          sx={{
            '&.MuiDialogContent-root': {
              backgroundImage: 'none',
            },
          }}
        >
          <Typography variant="h6">
            {submitAttempted && !error
              ? `Please continue to compete in the tournament.`
              : `Please pick a custom username to continue.`}
          </Typography>
          {submitAttempted && !error ? null : (
            <FormControl fullWidth sx={{ my: 4 }}>
              <TextField
                onChange={(e) => {
                  setNewUsername((prev) => {
                    return {
                      ...prev,
                      username: e.target.value,
                    };
                  });
                }}
                error={error || !newUsername.valid}
                fullWidth
                label={'New username'}
                helperText={
                  error
                    ? 'The requested username is not available. Please try again.'
                    : 'Usernames must be 12 characters or less. Dashes and underscores are allowed.'
                }
              ></TextField>
            </FormControl>
          )}
        </DialogContent>
        <DialogActions
          sx={{ justifyContent: 'center', width: '100%', mt: 4, pb: 0, px: 0 }}
        >
          {submitAttempted && !error ? (
            <Button
              variant="contained"
              size="large"
              sx={{ flex: 1 }}
              onClick={() => {
                return go('RulesCheckInDialog');
              }}
            >
              <Typography variant="h6" sx={{ color: 'white' }}>
                Continue
              </Typography>
            </Button>
          ) : (
            <LoadingButton
              variant="contained"
              size="large"
              disabled={!newUsername.valid}
              sx={{ flex: 1 }}
              onClick={changeUsername}
            >
              <Typography variant="h6" sx={{ color: 'white' }}>
                Change username
              </Typography>
            </LoadingButton>
          )}
        </DialogActions>
      </>
    );
  },
);
